import { useEffect, useMemo } from 'react';
import { useCropMonitorings } from '/@/hooks/crop-monitoring/use-crop-monitorings';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { getHybridProductName } from '/@/utils/crop-monitoring';

/** `CustomPaginator.max_page_size` — the server clamps anything larger to this. */
const PAGE_SIZE = 10000;

export type LastCampaign = {
    /** Harvested yield of that campaign, in kg/ha. */
    averageYield: number;
    plantingDate: string | null;
    productName?: string;
    crop: CropMonitoring['crop'];
};

/** Most recent crop monitoring, from a list already sorted newest-first, that was actually harvested. */
const extractLastCampaign = (cropMonitorings: CropMonitoring[]): LastCampaign | undefined => {
    const harvested = cropMonitorings.find((cropMonitoring) => !!cropMonitoring.result?.averageYield);
    if (!harvested?.result) return undefined;
    return {
        averageYield: harvested.result.averageYield,
        plantingDate: harvested.plantingDate ?? null,
        productName: getHybridProductName(harvested),
        crop: harvested.crop,
    };
};

/**
 * Last harvested corn campaign for a field — the most recent crop monitoring that
 * actually produced a harvest result. Fields with no monitoring history (or with
 * monitorings that were never harvested) simply resolve to `undefined`.
 */
export const useLastCampaign = (fieldId?: number): { lastCampaign?: LastCampaign; isLoading: boolean } => {
    const { data, isLoading } = useCropMonitorings({
        page: 1,
        pageSize: 20,
        fieldId: fieldId ? String(fieldId) : undefined,
        crop: 'corn',
        ordering: '-planting_date',
    });

    const lastCampaign = useMemo(() => {
        if (!fieldId) return undefined;
        return extractLastCampaign(data?.results || []);
    }, [data?.results, fieldId]);

    return { lastCampaign, isLoading };
};

/**
 * The five members `extractLastCampaign` and the caption actually read. Without this the list
 * view serializes the full crop-monitoring shape per row — the nested corn monitoring (a query
 * per row, plus its stages and yield estimations), the zone, and the nitrogen-recommendation
 * M2M — none of which is rendered here.
 */
const LAST_CAMPAIGN_FIELDS = 'id,field,planting_date,crop,product_name,result';

/**
 * Last harvested campaign for a LIST of fields (any crop — corn, soy, etc.), resolved with a
 * single request scoped by node (mirrors the node/includeChildren/excludeTargetNode scoping used
 * to fetch the field list itself) — avoids firing one crop-monitoring request per row.
 *
 * `hasResult` narrows the rows to harvested monitorings (the only ones this can resolve a
 * campaign from) and `selectOnly` narrows each row to the handful of columns below, so the
 * whole node subtree stays a small response instead of the full history. The page size is
 * deliberately generous: the answer is the latest campaign *per field*, so truncating by a
 * global `-planting_date` ordering drops fields whose last harvest is older.
 *
 * It is not unbounded, though — `PAGE_SIZE` sits on `CustomPaginator.max_page_size`, so a node
 * subtree with more harvested monitorings than that still truncates, and precisely in the way
 * described above. That is logged rather than papered over: resolving it needs either real
 * paging here or a server-side "latest per field" endpoint, and callers that hit it should fall
 * back to the per-field `useLastCampaign`.
 */
export const useLastCampaignsBulk = ({
    node,
    includeChildren,
    excludeTargetNode,
}: {
    node?: number;
    includeChildren?: boolean;
    excludeTargetNode?: boolean;
}): { lastCampaigns: Map<number, LastCampaign>; isLoading: boolean } => {
    const { data, isLoading } = useCropMonitorings({
        page: 1,
        pageSize: PAGE_SIZE,
        node,
        includeChildren,
        excludeTargetNode,
        ordering: '-planting_date',
        hasResult: true,
        selectOnly: LAST_CAMPAIGN_FIELDS,
    });

    const totalCount = data?.count;
    const fetchedCount = data?.results?.length;

    useEffect(() => {
        if (totalCount === undefined || fetchedCount === undefined) return;
        if (totalCount <= fetchedCount) return;
        console.warn(
            `useLastCampaignsBulk: ${totalCount} harvested monitorings exceed the ${PAGE_SIZE}-row page size; ` +
                'the oldest-harvest fields are missing their last campaign.'
        );
    }, [totalCount, fetchedCount]);

    const lastCampaigns = useMemo(() => {
        const byField = new Map<number, CropMonitoring[]>();
        for (const cropMonitoring of data?.results || []) {
            const fieldCropMonitorings = byField.get(cropMonitoring.field) || [];
            fieldCropMonitorings.push(cropMonitoring);
            byField.set(cropMonitoring.field, fieldCropMonitorings);
        }

        const result = new Map<number, LastCampaign>();
        for (const [fieldId, cropMonitorings] of byField) {
            const lastCampaign = extractLastCampaign(cropMonitorings);
            if (lastCampaign) result.set(fieldId, lastCampaign);
        }
        return result;
    }, [data?.results]);

    return { lastCampaigns, isLoading };
};
